import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Wheat, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About", id: "about" },
  { to: "/commodities", label: "Commodities", id: "commodities" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/quality", label: "Quality", id: "quality" },
  { to: "/markets", label: "Markets", id: "markets" },
  { to: "/logistics", label: "Logistics", id: "logistics" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [whatsapp, setWhatsapp] = useState("919000000000");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => setWhatsapp(data.business_whatsapp || "919848490057")).catch(() => {});
  }, []);

  const waHref = `https://wa.me/${whatsapp}?text=${encodeURIComponent("Hello Aaha Kanakam Agri Traders, I would like to enquire about your agricultural commodities.")}`;

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-forest/95 backdrop-blur-md shadow-[0_6px_24px_-16px_rgba(0,0,0,0.4)]" : "bg-forest"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-full bg-gold flex items-center justify-center text-forest">
              <Wheat size={22} strokeWidth={2} />
            </div>
            <div className="text-white leading-tight">
              <div className="font-serif text-xl tracking-tight">Aaha Kanakam</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Agri Traders</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide link-underline ${isActive ? "text-gold" : "text-white/85 hover:text-white"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-whatsapp"
              className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center text-white hover:bg-white hover:text-forest transition"
              aria-label="WhatsApp"
            >
              <MessageCircle size={17} />
            </a>
            <Link
              to="/quotation"
              data-testid="nav-cta-quote"
              className="btn-gold px-5 py-2.5 text-sm font-semibold tracking-wide rounded-none"
            >
              Request a Quotation
            </Link>
            {user && (
              <button
                data-testid="nav-logout"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-white/80 text-xs uppercase tracking-widest hover:text-gold"
              >
                Logout
              </button>
            )}
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-toggle" className="lg:hidden text-white p-2" aria-label="Menu">
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-forest text-white border-none w-[85%] sm:w-[380px]">
              <div className="flex items-center justify-between mb-8">
                <div className="font-serif text-2xl">Menu</div>
                <button onClick={() => setOpen(false)} className="text-white/80"><X size={22} /></button>
              </div>
              <div className="flex flex-col gap-1">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.id}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `py-3 px-2 border-b border-white/10 text-base ${isActive ? "text-gold" : "text-white/90"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/quotation"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-cta-quote"
                  className="btn-gold mt-6 px-5 py-3 text-sm font-semibold text-center tracking-wide"
                >
                  Request a Quotation
                </Link>
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-light mt-3 px-5 py-3 text-sm text-center"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
